import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API, SHOW_PRICING } from '../App';
import { DashboardLayout } from '../components/DashboardLayout';
import PrerequisiteGate from '../components/PrerequisiteGate';
import AutofillBanner from '../components/AutofillBanner';
import { useUserContext } from '../hooks/useUserContext';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Instagram, Loader2, Grid, Camera, BookOpen, Clock, Hash, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import StepReviewModal from '../components/StepReviewModal';

const InstagramStrategyContent = () => {
  const { t } = useTranslation();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [strategy, setStrategy] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [hasSeenResults, setHasSeenResults] = useState(false);
  
  // Auto-fill context
  const { 
    hasData: hasContextData, 
    sources, 
    sourcesDisplay, 
    summaryMessage 
  } = useUserContext();

  const tier = user?.subscription_tier || 'free';
  const subscriptionStatus = user?.subscription_status || 'inactive';
  const isBetaUser = user?.is_beta_user || user?.beta_access || subscriptionStatus === 'beta';
  // During beta (SHOW_PRICING=false), all users have access
  const hasAccess = !SHOW_PRICING || tier === 'premium' || tier === 'pro' || tier === 'experience' || isBetaUser;

  useEffect(() => {
    if (hasAccess) {
      fetchLatestStrategy();
    }
  }, [hasAccess]);

  const fetchLatestStrategy = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/instagram-strategy/latest`, { headers });
      if (response.data.strategy) {
        setStrategy(response.data.strategy);
      }
    } catch (error) {
      console.error('Error fetching strategy:', error);
    }
  };

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(`${API}/instagram-strategy/generate`, {}, { headers });
      setStrategy(response.data);
      setHasSeenResults(false); // User hasn't seen results yet
      toast.success('Stratégie Instagram générée !');
      // Review will be shown after user clicks "J'ai compris" button
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  // During beta, show coming soon message instead of paywall
  if (!hasAccess) {
    return (
      <DashboardLayout title="Instagram Visual Strategy">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card-premium p-12 text-center"
          >
            <Sparkles className="text-[#D4AF37] mx-auto mb-6" size={64} />
            <h2 className="font-serif text-2xl text-white mb-4">Bientôt disponible</h2>
            <p className="text-[#A1A1AA] mb-8 max-w-md mx-auto">
              Cette fonctionnalité sera bientôt accessible. Continue ton parcours pour débloquer de nouvelles possibilités.
            </p>
            <Button
              onClick={() => navigate('/dashboard')}
              className="btn-gold px-8 py-4"
              data-testid="back-dashboard-btn"
            >
              Retour au parcours
            </Button>
          </motion.div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Stratégie Instagram">
      <div className="max-w-6xl mx-auto">
        {/* Auto-fill Banner */}
        {hasContextData && !strategy && (
          <AutofillBanner
            hasData={hasContextData}
            sources={sources}
            sourcesDisplay={sourcesDisplay}
            message={summaryMessage}
            showDetails={true}
          />
        )}
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card-premium p-8 mb-8"
        >
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <Instagram className="text-[#E5C585] mb-4" size={28} />
              <h2 className="font-serif text-2xl text-white mb-2">Générateur de Stratégie Instagram</h2>
              <p className="text-[#A1A1AA]">
                Génère une stratégie visuelle complète pour ta présence Instagram.
              </p>
            </div>
            <Button
              onClick={handleGenerate}
              disabled={loading}
              className="btn-gold px-8 py-4 flex items-center gap-2"
              data-testid="generate-strategy-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin" size={18} />
                  Generating...
                </>
              ) : strategy ? (
                'Regenerate Strategy'
              ) : (
                'Generate Strategy'
              )}
            </Button>
          </div>
        </motion.div>

        {/* Strategy Content */}
        {strategy ? (
          <div className="space-y-6">
            {/* Feed Style */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="card-premium p-8"
            >
              <div className="flex items-center gap-3 mb-4">
                <Grid className="text-[#E5C585]" size={24} />
                <h3 className="font-serif text-xl text-white">Feed Style</h3>
              </div>
              <p className="text-[#A1A1AA] leading-relaxed">{strategy.feed_style}</p>
            </motion.div>

            {/* Photo Types & Content Themes */}
            <div className="grid md:grid-cols-2 gap-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                className="card-premium p-8"
              >
                <div className="flex items-center gap-3 mb-4">
                  <Camera className="text-[#D4AF37]" size={24} />
                  <h3 className="font-serif text-xl text-white">{t('instagramStrategy.photoTypes')}</h3>
                </div>
                <ul className="space-y-3">
                  {strategy.photo_types?.map((type, i) => (
                    <li key={i} className="text-[#A1A1AA] flex items-start gap-3">
                      <span className="text-[#D4AF37] mt-1">•</span>
                      {type}
                    </li>
                  ))}
                </ul>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="card-premium p-8"
              >
                <div className="flex items-center gap-3 mb-4">
                  <BookOpen className="text-[#E5C585]" size={24} />
                  <h3 className="font-serif text-xl text-white">{t('instagramStrategy.contentThemes')}</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {strategy.content_themes?.map((theme, i) => (
                    <span 
                      key={i} 
                      className="px-4 py-2 bg-[#1C1C1C] text-[#A1A1AA] text-sm border border-[#27272A]"
                    >
                      {theme}
                    </span>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* Storytelling Direction */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
              className="card-premium p-8"
            >
              <div className="flex items-center gap-3 mb-4">
                <BookOpen className="text-[#D4AF37]" size={24} />
                <h3 className="font-serif text-xl text-white">{t('instagramStrategy.storytellingDirection')}</h3>
              </div>
              <p className="text-[#A1A1AA] leading-relaxed">{strategy.storytelling_direction}</p>
            </motion.div>

            {/* Posting Schedule & Hashtags */}
            <div className="grid md:grid-cols-2 gap-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="card-premium p-8"
              >
                <div className="flex items-center gap-3 mb-4">
                  <Clock className="text-[#E5C585]" size={24} />
                  <h3 className="font-serif text-xl text-white">{t('instagramStrategy.postingSchedule')}</h3>
                </div>
                <div className="space-y-4">
                  <div className="flex justify-between items-center border-b border-[#27272A] pb-3">
                    <span className="text-[#A1A1AA]">{t('instagramStrategy.postsPerWeek')}</span>
                    <span className="text-white font-serif text-xl">{strategy.posting_schedule?.posts_per_week}</span>
                  </div>
                  <div>
                    <span className="text-[#A1A1AA] text-sm block mb-2">{t('instagramStrategy.bestTimes')}</span>
                    <div className="flex gap-2">
                      {strategy.posting_schedule?.best_times?.map((time, i) => (
                        <span key={i} className="px-3 py-1 bg-[#1C1C1C] text-white text-sm border border-[#27272A]">
                          {time}
                        </span>
                      ))}
                    </div>
                  </div>
                  <p className="text-[#A1A1AA] text-sm">{strategy.posting_schedule?.content_mix}</p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 }}
                className="card-premium p-8"
              >
                <div className="flex items-center gap-3 mb-4">
                  <Hash className="text-[#D4AF37]" size={24} />
                  <h3 className="font-serif text-xl text-white">{t('instagramStrategy.hashtagStrategy')}</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {strategy.hashtag_strategy?.map((tag, i) => (
                    <span 
                      key={i} 
                      className="px-3 py-1 bg-[#D4AF37]/10 text-[#E5C585] text-sm border border-[#D4AF37]/30"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </motion.div>

              {/* Call to Action - Triggers Review */}
              {!hasSeenResults && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="card-gold p-6 text-center"
                >
                  <p className="text-white mb-4">As-tu pris le temps de découvrir ta stratégie ?</p>
                  <Button
                    onClick={() => {
                      setHasSeenResults(true);
                      setShowReviewModal(true);
                    }}
                    className="btn-gold px-8 py-3"
                    data-testid="understood-btn"
                  >
                    J'ai compris, donner mon avis ✨
                  </Button>
                </motion.div>
              )}
            </div>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="card-premium p-16 text-center"
          >
            <Instagram className="text-[#27272A] mx-auto mb-6" size={80} />
            <h3 className="font-serif text-2xl text-white mb-4">{t('instagramStrategy.noStrategyYet')}</h3>
            <p className="text-[#A1A1AA] max-w-md mx-auto">
              {t('instagramStrategy.noStrategyDesc')}
            </p>
          </motion.div>
        )}
      </div>

      {/* Step Review Modal */}
      <StepReviewModal
        isOpen={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        stepId="growth_strategy"
        stepName="Te rendre visible"
        onValidated={() => setShowReviewModal(false)}
      />
    </DashboardLayout>
  );
};

// Wrap with PrerequisiteGate
const InstagramStrategy = () => {
  return (
    <PrerequisiteGate 
      toolId="instagram-strategy" 
      toolTitle="Stratégie Instagram"
    >
      <InstagramStrategyContent />
    </PrerequisiteGate>
  );
};

export default InstagramStrategy;
